/**
 * TileEditor — Chakra Drawer slide-over for create/edit of a tile.
 * Fields are dynamic per (source, type). Save calls POST/PUT against /boards.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  Button,
  IconButton,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  useToast,
  Checkbox,
  CheckboxGroup,
  Divider
} from '@chakra-ui/react';
import { FiTrash2, FiPlus, FiInfo } from 'react-icons/fi';
import ReactSelect from 'react-select';
import { boardsApi } from './api';
import EventsPicker from './components/EventsPicker';
import SearchableSelect from './components/SearchableSelect';

// Shared ReactSelect styles — chips use brand blue, options highlight on hover/select
const RS_STYLES = {
  multiValue: (base) => ({
    ...base,
    background: 'var(--brand-tint)',
    borderRadius: '6px',
    border: '1px solid var(--brand-border)',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'var(--brand-primary)',
    fontWeight: 600,
    fontSize: '12px',
    paddingLeft: '8px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--brand-primary)',
    borderRadius: '0 6px 6px 0',
    ':hover': { background: 'var(--brand-tint-strong)', color: 'var(--brand-primary-hover)' },
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-tint)' : 'white',
    color: state.isSelected ? 'white' : '#0f172a',
    fontSize: '14px',
    cursor: 'pointer',
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? 'var(--brand-primary)' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 1px var(--brand-primary)' : 'none',
    borderRadius: '6px',
    ':hover': { borderColor: 'var(--brand-primary)' },
  }),
  menuPortal: (b) => ({ ...b, zIndex: 99999 }),
};

// Sub-modes for the breakdown tile type. The tile type itself stays 'breakdown';
// each mode pivots the renderer + the editor form so a user can configure every
// shape from this panel alone (no hard-coded tiles).
const BREAKDOWN_MODES = [
  { value: 'standard',    label: 'Standard breakdown' },
  { value: 'dropoff',     label: 'Drop-off (between stages)' },
  { value: 'combo',       label: 'Volume + drop-off (combo)' },
  { value: 'sweep',       label: 'Sweep analysis (full-sweep within N days)' },
  { value: 'attribution', label: 'Cross-event attribution' },
];

const TYPES_BY_SOURCE = {
  events:            ['big_number', 'time_series', 'funnel', 'breakdown', 'distribution', 'kpi_table', 'flow', 'heatmap'],
  contacts:          ['channel_reachability', 'profile_breakdown', 'big_number'],
  cohort_membership: ['segment_leaderboard', 'big_number'],
};

// Top-level event columns (vs `variables['<key>']` map keys). Mirrors Event/board-tile TOP_LEVEL_PROPS.
const TOP_LEVEL_COLUMNS = [
  { name: 'event',       type: 'string', column: true },
  { name: 'contact',     type: 'string', column: true },
  { name: 'channel',     type: 'string', column: true },
  { name: 'country',     type: 'string', column: true },
  { name: 'session_id',  type: 'string', column: true },
  { name: 'distinct_id', type: 'string', column: true },
];

// Business-friendly display labels (used in dropdowns, badges, and the cohort-scope helper).
const SOURCE_LABELS = {
  events:            'Events',
  contacts:          'Contacts',
  cohort_membership: 'Cohort Membership',
};
const SOURCE_HELPERS = {
  events:            'Behavioural events (logins, transactions, declines, OTPs, etc.)',
  contacts:          'Static customer attributes & reachability (phone, email, tags)',
  cohort_membership: 'Segment sizes & memberships (e.g. Salaried Professionals, VIP)',
};
const TYPE_LABELS = {
  big_number:           'Big Number',
  time_series:          'Area Chart',
  funnel:               'Funnel',
  breakdown:            'Breakdown',
  kpi_table:            'KPI Table',
  segment_leaderboard:  'Segment Leaderboard',
  profile_breakdown:    'Profile Breakdown',
  channel_reachability: 'Channel Reachability',
  retention:            'Retention',
  distribution:         'Distribution',
  flow:                 'User Flow',
  path:                 'Path / Sankey',
};
const niceType = (t) => TYPE_LABELS[t] || (t || '').split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
const niceSource = (s) => SOURCE_LABELS[s] || (s || '').split('_').map(x => x.charAt(0).toUpperCase() + x.slice(1)).join(' ');

// "Chart" card type — a UI umbrella consolidating the chart visualizations under one
// Card Type + a "Chart Type" sub-dropdown. The underlying tile.type stays a real
// renderer type (time_series/breakdown/funnel/distribution/heatmap) — "chart" is never
// stored; the Card Type dropdown just shows these as a single "Chart" entry.
const CHART_TYPES = ['time_series', 'breakdown', 'distribution', 'heatmap', 'funnel'];
// Chart Type options → underlying tile.type (+ optional viz_config mode).
const CHART_TYPE_OPTIONS = [
  { value: 'area',         label: 'Area / Line (trend over time)', type: 'time_series' },
  { value: 'stacked_area', label: 'Stacked area (over time)',      type: 'time_series',  chart: 'stacked_area' },
  { value: 'bar',          label: 'Bar chart',                     type: 'breakdown',    mode: 'standard' },
  { value: 'stacked',      label: 'Stacked (volume + drop-off)',   type: 'breakdown',    mode: 'combo' },
  { value: 'pie',          label: 'Pie',                           type: 'breakdown',    mode: 'standard', chart: 'pie' },
  { value: 'doughnut',     label: 'Doughnut',                      type: 'breakdown',    mode: 'standard', chart: 'doughnut' },
  { value: 'funnel',       label: 'Funnel',                        type: 'funnel' },
  { value: 'distribution', label: 'Distribution',                  type: 'distribution' },
  { value: 'heatmap',      label: 'Heatmap',                       type: 'heatmap' },
];
// Card Type categories per source — chart types collapse into one "Chart" entry.
const CARD_TYPES_BY_SOURCE = {
  events:            ['big_number', 'chart', 'kpi_table', 'flow'],
  contacts:          ['channel_reachability', 'profile_breakdown', 'big_number'],
  cohort_membership: ['segment_leaderboard', 'big_number'],
};
const CATEGORY_LABELS = { ...TYPE_LABELS, chart: 'Chart' };
const niceCategory = (c) => CATEGORY_LABELS[c] || niceType(c);

const TIME_FRAMES = [
  { value: 'last_24_hours', label: 'Last 24 hours' },
  { value: 'last_7_days',   label: 'Last 7 days' },
  { value: 'last_30_days',  label: 'Last 30 days' },
  { value: 'last_90_days',  label: 'Last 90 days' },
  { value: 'last_year',     label: 'Last year' },
];

const METRICS = [
  { value: 'count',           label: 'Event count' },
  { value: 'unique_contacts', label: 'Unique customers' },
  { value: 'sum',             label: 'Sum of a numeric property' },
];

// Numeric-property detection for Sum / Average / Distribution dropdowns (Bug-003, Bug-015).
// The backend only NAME-infers types (amount/price/cost → number; everything else → string),
// so a plain type filter is useless. We widen with a quantity-name allowlist and, crucially,
// exclude identifier/code fields that are digits but NOT summable (OTP, PIN, id, code, ref).
// `type === 'number'` always qualifies; text/date/boolean names are rejected first.
const NUMERIC_NAME_RE = /(amount|amt|price|cost|total|balance|revenue|spend|income|salary|value|score|qty|quantity|count|duration|weight|volume|units?|fees?|charge|discount|tax|payment|limit|points?)/i;
const TEXT_NAME_RE = /(name|email|phone|mobile|msisdn|city|country|region|gender|status|channel|type|code|message|reason|title|url|address|_id|uuid|token|description|label|category|otp|pin|cvv|reference|ref_?no|\bid\b)/i;
function isNumericAttr(a) {
  if (!a || !a.name) return false;
  if (a.type === 'number') return true;         // explicit numeric wins
  if (a.type === 'date' || a.type === 'boolean') return false;
  if (TEXT_NAME_RE.test(a.name)) return false;  // identifiers/codes/text — never summable
  return NUMERIC_NAME_RE.test(a.name);
}

function emptyTile() {
  return {
    title: '',
    type: 'big_number',
    source: 'events',
    cohort_scope: null,
    refresh_frequency: 'daily',
    viz_config: { event: '', metric: 'count', time_frame: 'last_30_days', compare_to: 'previous_period' },
  };
}

export default function TileEditor({ isOpen, onClose, mode, boardId, tile, onSaved, cohorts }) {
  const toast = useToast();
  const [draft, setDraft] = useState(emptyTile());
  const [saving, setSaving] = useState(false);
  // Defensive guards — never crash if a prop or fetch returns a non-array.
  const cohortList = Array.isArray(cohorts) ? cohorts : [];

  // ── Events + property dropdown state ──
  const [events, setEvents] = useState([]);          // [{name, display_name}, ...]
  const [eventsLoading, setEventsLoading] = useState(false);
  const [attributes, setAttributes] = useState([]);  // attrs for the MAIN event
  const [attrsLoading, setAttrsLoading] = useState(false);
  // Per-event attribute cache, used by sweep / attribution forms that need
  // properties from a SECOND event (sink_event, attribute_via.event, etc.).
  const [attrsCache, setAttrsCache] = useState({});
  const fetchAttrsFor = async (ev) => {
    if (!ev || attrsCache[ev]) return;
    try {
      const r = await boardsApi.listEventAttributes(ev);
      setAttrsCache((c) => ({ ...c, [ev]: Array.isArray(r?.data) ? r.data : [] }));
    } catch (_) { /* keep silent — UI just shows the existing value */ }
  };

  // Lazy-load events the first time the editor opens (and only when needed).
  useEffect(() => {
    if (!isOpen) return;
    if (events.length > 0) return;
    setEventsLoading(true);
    boardsApi.listEvents()
      .then((r) => setEvents(Array.isArray(r?.data) ? r.data : []))
      .finally(() => setEventsLoading(false));
  }, [isOpen]);  // eslint-disable-line react-hooks/exhaustive-deps

  // All events selected on this tile (single OR multi). The property dropdowns must union
  // properties across EVERY selected event, not just the first (Bug-007).
  const selectedEvents = useMemo(() => {
    if (draft.source !== 'events') return [];
    if (Array.isArray(draft.viz_config?.events) && draft.viz_config.events.length) {
      return draft.viz_config.events.filter(Boolean);
    }
    if (draft.viz_config?.event) return [draft.viz_config.event];
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.source, draft.viz_config?.event, (draft.viz_config?.events || []).join('|')]);

  // Fetch attributes for EVERY selected event into attrsCache (so the merged dropdown below
  // sees all of them). `attributes` is kept = primary event for any legacy single-event reads.
  useEffect(() => {
    if (draft.source !== 'events' || selectedEvents.length === 0) { setAttributes([]); return; }
    let cancelled = false;
    setAttrsLoading(true);
    Promise.all(selectedEvents.map(async (ev) => {
      if (attrsCache[ev]) return { ev, list: attrsCache[ev] };
      const r = await boardsApi.listEventAttributes(ev);
      return { ev, list: Array.isArray(r?.data) ? r.data : [] };
    }))
      .then((results) => {
        if (cancelled) return;
        setAttrsCache((c) => {
          const next = { ...c };
          results.forEach(({ ev, list }) => { next[ev] = list; });
          return next;
        });
        setAttributes(results[0]?.list || []);
      })
      .finally(() => { if (!cancelled) setAttrsLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.source, selectedEvents.join('|')]);

  // Tenant profile keys for the Profile Breakdown "Profile key" dropdown (Bug-019).
  const [profileKeys, setProfileKeys] = useState([]);
  useEffect(() => {
    if (!isOpen || draft.source !== 'contacts') return;
    if (profileKeys.length > 0) return;
    boardsApi.listProfileKeys().then((r) => setProfileKeys(Array.isArray(r?.data) ? r.data : []));
  }, [isOpen, draft.source]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Union of properties across all selected events, de-duped by name (Bug-007).
  const mergedAttributes = useMemo(() => {
    const seen = new Map();
    for (const ev of selectedEvents) {
      for (const a of (attrsCache[ev] || [])) {
        if (a && a.name && !seen.has(a.name)) seen.set(a.name, a);
      }
    }
    if (seen.size === 0) for (const a of attributes) if (a && a.name && !seen.has(a.name)) seen.set(a.name, a);
    return Array.from(seen.values());
  }, [selectedEvents, attrsCache, attributes]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && tile) {
      setDraft({
        title: tile.title || '',
        type: tile.type,
        source: tile.source,
        cohort_scope: tile.cohort_scope ? String(tile.cohort_scope) : null,
        refresh_frequency: tile.refresh_frequency || 'daily',
        viz_config: { ...(tile.viz_config || {}) },
      });
    } else {
      setDraft(emptyTile());
    }
  }, [isOpen, mode, tile]);

  const setVc = (k, v) => setDraft((d) => ({ ...d, viz_config: { ...d.viz_config, [k]: v } }));
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // ── Card Type ⇄ Chart Type layer ──
  // Chart visualizations collapse to a single "Chart" category in the Card Type
  // dropdown; the "Chart Type" sub-dropdown picks the real tile.type (+ mode).
  const cardTypeOptions = (CARD_TYPES_BY_SOURCE[draft.source] || TYPES_BY_SOURCE[draft.source] || [])
    // Segment Leaderboard ranks ALL cohorts against each other — it's meaningless scoped to a
    // single cohort, so it's not offered once a specific cohort is selected (Bug-001 / Bug-009).
    .filter((t) => !(t === 'segment_leaderboard' && draft.cohort_scope));
  const currentCategory = CHART_TYPES.includes(draft.type) ? 'chart' : draft.type;
  const handleCardTypeChange = (cat) => {
    if (cat === 'chart') { setField('type', 'time_series'); return; } // default chart type = Area/Line
    // Picking the leaderboard clears any cohort scope (it's a whole-base, all-cohorts ranking).
    if (cat === 'segment_leaderboard') { setDraft((d) => ({ ...d, type: cat, cohort_scope: null })); return; }
    setField('type', cat);
  };
  const currentChartType = (() => {
    const ch = draft.viz_config?.chart;
    if (ch === 'pie' || ch === 'doughnut' || ch === 'stacked_area') return ch;
    if (draft.type === 'breakdown') {
      return (draft.viz_config?.mode === 'combo' || draft.viz_config?.mode === 'dropoff') ? 'stacked' : 'bar';
    }
    return CHART_TYPE_OPTIONS.find((o) => o.type === draft.type)?.value || 'area';
  })();
  const handleChartTypeChange = (v) => {
    const opt = CHART_TYPE_OPTIONS.find((o) => o.value === v);
    if (!opt) return;
    setDraft((d) => {
      const vc = { ...d.viz_config };
      if (opt.mode) vc.mode = opt.mode;
      if (opt.chart) vc.chart = opt.chart; else delete vc.chart; // clear flag for plain area/bar/funnel
      return { ...d, type: opt.type, viz_config: vc };
    });
  };

  // Bug-026: Sweep analysis & Cross-event attribution render only as Table/Cards —
  // a chart type has no effect there, so the Chart Type selector must be hidden
  // (otherwise the panel shows a Bar/Area choice that is silently ignored). The
  // underlying type stays 'breakdown', so switching Mode back to Standard restores
  // the Chart Type field with its prior value.
  const chartMode = draft.type === 'breakdown' ? (draft.viz_config?.mode || 'standard') : null;
  const chartTypeApplies = !(chartMode === 'sweep' || chartMode === 'attribution');

  // Source-change → reset type to first allowed + clear source-specific viz_config keys
  const handleSourceChange = (newSource) => {
    const firstType = TYPES_BY_SOURCE[newSource][0];
    let vc = {};
    if (newSource === 'events') vc = { event: '', metric: 'count', time_frame: 'last_30_days', compare_to: 'previous_period' };
    if (newSource === 'contacts') vc = {};
    if (newSource === 'cohort_membership') vc = { top_n: 10 };
    setDraft((d) => ({ ...d, source: newSource, type: firstType, viz_config: vc }));
  };

  const save = async () => {
    // Light validation. Backend re-validates server-side.
    if (!draft.title.trim()) return toast({ status: 'warning', title: 'Title required' });
    const bdMode = draft.type === 'breakdown' ? (draft.viz_config.mode || 'standard') : null;
    if (draft.source === 'events' && draft.type !== 'kpi_table' && draft.type !== 'funnel' && draft.type !== 'flow') {
      // Sweep mode owns its own source/sink events — not event/events.
      if (!(draft.type === 'breakdown' && bdMode === 'sweep')) {
        const hasSingle = !!draft.viz_config.event;
        const hasMulti  = Array.isArray(draft.viz_config.events) && draft.viz_config.events.length > 0;
        if (!hasSingle && !hasMulti) {
          return toast({ status: 'warning', title: 'Pick at least one event for this tile type' });
        }
      }
    }
    if (draft.type === 'distribution' && !draft.viz_config.property) {
      return toast({ status: 'warning', title: 'Distribution needs a numeric property to bucket' });
    }
    if (draft.type === 'breakdown') {
      if (bdMode === 'standard' && !draft.viz_config.property) {
        return toast({ status: 'warning', title: 'Breakdown needs a "break down by" property' });
      }
      if (bdMode === 'sweep') {
        const vc = draft.viz_config;
        if (!vc.source_event)   return toast({ status: 'warning', title: 'Sweep needs a source event' });
        if (!vc.sink_event)     return toast({ status: 'warning', title: 'Sweep needs a sink event' });
        if (!vc.sink_breakdown) return toast({ status: 'warning', title: 'Sweep needs a "group by" property on the sink side' });
      }
      if ((bdMode === 'dropoff' || bdMode === 'combo')
        && !(Array.isArray(draft.viz_config.events) && draft.viz_config.events.length >= 2)) {
        return toast({ status: 'warning', title: 'Drop-off / combo modes need ≥ 2 events in order' });
      }
      if (bdMode === 'attribution') {
        const via = draft.viz_config.attribute_via || {};
        if (!via.event || !via.property)
          return toast({ status: 'warning', title: 'Attribution needs an "attribute via" event and property' });
      }
    }
    if (draft.type === 'funnel') {
      // New picker writes draft.viz_config.steps directly. Keep the legacy stepsText
      // path as a fallback for any cached drafts mid-migration.
      let steps = Array.isArray(draft.viz_config.steps) ? draft.viz_config.steps.filter(Boolean) : [];
      if (steps.length < 2 && draft.viz_config.stepsText) {
        steps = draft.viz_config.stepsText.split(',').map((s) => s.trim()).filter(Boolean);
      }
      if (steps.length < 2) return toast({ status: 'warning', title: 'Funnel needs ≥ 2 step events (pick in order)' });
      draft.viz_config.steps = steps;
      delete draft.viz_config.stepsText;
    }
    if (draft.type === 'flow' && !draft.viz_config.anchor_event) {
      return toast({ status: 'warning', title: 'Flow needs an anchor event' });
    }
    // Sum / Average must carry a property, else the tile saves but breaks silently at render
    // (Bug-011 / Bug-021). Covers single-metric ({metric,property}) + multi-metric (metrics[]).
    if (draft.source === 'events') {
      const ml = Array.isArray(draft.viz_config.metrics)
        ? draft.viz_config.metrics
        : (draft.viz_config.metric ? [{ agg: draft.viz_config.metric, property: draft.viz_config.property }] : []);
      const bad = ml.find((m) => (m.agg === 'sum' || m.agg === 'avg') && !(m.property && String(m.property).trim()));
      if (bad) {
        return toast({ status: 'warning', title: `Pick a property for "${bad.agg === 'avg' ? 'Average' : 'Sum'}"` });
      }
    }
    setSaving(true);
    try {
      // Coerce any react-select option objects back to plain strings so combo / drop-off / funnel
      // tiles always persist string event arrays. Editing a Stacked chart re-sent option objects
      // ({value,label}), which failed backend validation with a raw 400 (Bug-024).
      const toStr = (e) => (e && typeof e === 'object') ? (e.value ?? e.name ?? '') : e;
      const vcOut = { ...draft.viz_config };
      if (Array.isArray(vcOut.events)) vcOut.events = vcOut.events.map(toStr).filter(Boolean);
      if (Array.isArray(vcOut.steps))  vcOut.steps  = vcOut.steps.map(toStr).filter(Boolean);
      const payload = {
        title: draft.title.trim(),
        type: draft.type,
        source: draft.source,
        cohort_scope: draft.cohort_scope || null,
        refresh_frequency: draft.refresh_frequency,
        viz_config: vcOut,
      };
      let result;
      if (mode === 'edit') result = await boardsApi.updateTile(boardId, tile._id || tile.id, payload);
      else                  result = await boardsApi.createTile(boardId, payload);
      toast({ status: 'success', title: mode === 'edit' ? 'Tile updated' : 'Tile created', duration: 2000 });
      onSaved && onSaved(result.data);
      onClose();
    } catch (e) {
      toast({ status: 'error', title: 'Save failed', description: e?.response?.data?.error?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          <HStack spacing={2}>
            <Text>{mode === 'edit' ? 'Edit tile' : 'Add new tile'}</Text>
            <Badge bg="var(--brand-primary)" color="white" borderRadius="full">{niceSource(draft.source)}</Badge>
            <Badge variant="outline" borderColor="var(--brand-primary)" color="var(--brand-primary)" borderRadius="full">{niceType(draft.type)}</Badge>
          </HStack>
        </DrawerHeader>
        <DrawerCloseButton />
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Title</FormLabel>
              <Input value={draft.title} onChange={(e) => setField('title', e.target.value)} placeholder="e.g. Active Customers (30d)" />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Data source</FormLabel>
              <Select value={draft.source} onChange={(e) => handleSourceChange(e.target.value)}>
                {Object.keys(SOURCE_LABELS).map((s) => (
                  <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
                ))}
              </Select>
              {SOURCE_HELPERS[draft.source] && (
                <Text fontSize="xs" color="gray.500" mt={1}>{SOURCE_HELPERS[draft.source]}</Text>
              )}
            </FormControl>

            {/* Cohort scope (segment restriction) — directly under Data source, and
                ONLY for non-Events sources. Events tiles pick an event instead. */}
            {draft.source !== 'events' && (
              <FormControl>
                <FormLabel fontSize="sm">Cohort scope (optional)</FormLabel>
                <Select value={draft.cohort_scope || ''} onChange={(e) => setField('cohort_scope', e.target.value || null)} placeholder="— Whole base (no scope) —">
                  {cohortList.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </Select>

                {/* Info alert — full width, brand-blue palette */}
                <Box
                  mt={2}
                  px={3}
                  py={2.5}
                  bg="var(--brand-tint)"
                  border="1px solid var(--brand-border)"
                  borderLeft="3px solid var(--brand-primary)"
                  borderRadius="8px"
                  w="100%"
                >
                  <HStack spacing={1.5} mb={1} align="center">
                    <Box color="var(--brand-primary)" flexShrink={0}><FiInfo size={12} /></Box>
                    <Text fontSize="xs" fontWeight={700} color="var(--brand-primary-active)">What it does</Text>
                  </HStack>
                  <Text fontSize="xs" color="#3b82f6" lineHeight={1.7}>
                    Restricts this tile's calculation to a specific segment's members only.
                    E.g. choose{' '}
                    <Text as="span" fontWeight={700} color="var(--brand-primary-hover)">"High-Income Earners"</Text>
                    {' '}on a "Top merchants" tile to see where the high-income segment spends.
                    Leave empty to calculate across your{' '}
                    <Text as="span" fontWeight={600} color="var(--brand-primary-active)">entire customer base</Text>.
                  </Text>
                </Box>
              </FormControl>
            )}

            {/* Event(s) — directly under Data source for the simple Events card types
                (the breakdown/flow/sweep types pick their events further down, since
                they depend on the chosen card type / mode). */}
            {draft.source === 'events'
              && ['big_number', 'time_series', 'distribution', 'heatmap'].includes(draft.type) && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Event(s)</FormLabel>
                <EventsPicker
                  value={Array.isArray(draft.viz_config.events) ? draft.viz_config.events : draft.viz_config.event}
                  onChange={(next) => setDraft((d) => ({ ...d, viz_config: { ...d.viz_config, ...next } }))}
                  events={events}
                  loading={eventsLoading}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Pick one or more events. Multiple events are OR-combined (any of them counts).
                </Text>
              </FormControl>
            )}

            {/* Card type — chosen AFTER the event / cohort scope. Chart visualizations
                collapse into one "Chart" entry; the Chart Type sub-dropdown picks which. */}
            <FormControl>
              <FormLabel fontSize="sm">Card type</FormLabel>
              <Select value={currentCategory} onChange={(e) => handleCardTypeChange(e.target.value)}>
                {cardTypeOptions.map((c) => <option key={c} value={c}>{niceCategory(c)}</option>)}
              </Select>
            </FormControl>

            {/* Chart Type — shown only for a "Chart" card whose mode can render a chart. */}
            {currentCategory === 'chart' && chartTypeApplies && (
              <FormControl>
                <FormLabel fontSize="sm">Chart Type</FormLabel>
                <Select value={currentChartType} onChange={(e) => handleChartTypeChange(e.target.value)}>
                  {CHART_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Area/Line = trend over time · Bar/Stacked = category breakdown · Funnel · Distribution · Heatmap.
                </Text>
              </FormControl>
            )}
            {/* Bug-026 — Sweep / Cross-event attribution can't render a chart; explain
                instead of showing a control that would be silently ignored. */}
            {currentCategory === 'chart' && !chartTypeApplies && (
              <FormControl>
                <FormLabel fontSize="sm">Chart Type</FormLabel>
                <Text fontSize="xs" color="gray.500">
                  Chart type doesn’t apply in {chartMode === 'sweep' ? 'Sweep analysis' : 'Cross-event attribution'} mode — results render as a table or cards.
                </Text>
              </FormControl>
            )}

            {/* ── Source-specific config ── */}
            {/* Breakdown sub-mode picker — drives the rest of the breakdown form.
                Pie / Doughnut are always a plain standard breakdown, so the Mode selector
                (drop-off / combo / sweep / attribution) is hidden for them (Bug-027). */}
            {draft.source === 'events' && draft.type === 'breakdown'
              && !['pie', 'doughnut'].includes(draft.viz_config.chart) && (
              <FormControl>
                <FormLabel fontSize="sm">Mode</FormLabel>
                <Select value={draft.viz_config.mode || 'standard'}
                  onChange={(e) => setVc('mode', e.target.value)}>
                  {BREAKDOWN_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Standard = group + aggregate. Drop-off / combo = compare consecutive stages.
                  Sweep = trace money / signal from one event to another within N days.
                  Attribution = group by a property from a DIFFERENT event.
                </Text>
              </FormControl>
            )}

            {/* (The simple-Events picker now renders directly under "Data source" above.)
                Breakdown standard/attribution still pick events here, after the Mode select,
                since the event set depends on the chosen breakdown mode. */}
            {draft.source === 'events' && draft.type === 'breakdown'
              && ['standard', 'attribution'].includes(draft.viz_config.mode || 'standard') && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Event(s)</FormLabel>
                <EventsPicker
                  value={Array.isArray(draft.viz_config.events) ? draft.viz_config.events : draft.viz_config.event}
                  onChange={(next) => setDraft((d) => ({ ...d, viz_config: { ...d.viz_config, ...next } }))}
                  events={events}
                  loading={eventsLoading}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Pick one or more events. Multiple events are OR-combined (any of them counts).
                </Text>
              </FormControl>
            )}

            {/* Flow — anchor event + how many path columns to show before / after it. */}
            {draft.source === 'events' && draft.type === 'flow' && (
              <>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Anchor event</FormLabel>
                  <Select placeholder={eventsLoading ? 'Loading events…' : 'Pick the focal event'}
                    value={draft.viz_config.anchor_event || ''}
                    onChange={(e) => setVc('anchor_event', e.target.value)}>
                    {events.map((ev) => <option key={ev.name} value={ev.name}>{ev.display_name || ev.name}</option>)}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    The focal event. The flow shows the Sankey paths users took <em>before</em> and <em>after</em> it.
                  </Text>
                </FormControl>
                <HStack align="flex-start" spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Steps before</FormLabel>
                    <NumberInput min={0} max={8} value={draft.viz_config.steps_before ?? 3}
                      onChange={(_, n) => setVc('steps_before', Number.isFinite(n) ? n : 3)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Steps after</FormLabel>
                    <NumberInput min={0} max={8} value={draft.viz_config.steps_after ?? 3}
                      onChange={(_, n) => setVc('steps_after', Number.isFinite(n) ? n : 3)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Top per step</FormLabel>
                    <NumberInput min={1} max={30} value={draft.viz_config.top_n_per_step ?? 5}
                      onChange={(_, n) => setVc('top_n_per_step', Number.isFinite(n) ? n : 5)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Before / after are each capped at 0–8 columns; top-N events per column (1–30). Mixpanel preset: 3 / 3 / 5.
                </Text>
              </>
            )}

            {/* Heatmap — metric for the day-of-week × hour grid (event picker is shared above). */}
            {draft.source === 'events' && draft.type === 'heatmap' && (
              <FormControl>
                <FormLabel fontSize="sm">Metric</FormLabel>
                <Select value={draft.viz_config.metric || 'count'} onChange={(e) => setVc('metric', e.target.value)}>
                  <option value="count">Event count</option>
                  <option value="unique_contacts">Unique customers</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Plots the event(s) above as a day-of-week × hour grid. Axes are fixed; pick the metric here.
                </Text>
              </FormControl>
            )}

            {/* Reusable "metrics on this card" group — big_number, distribution,
                and breakdown (standard + attribution modes only; sweep + dropoff + combo
                have their own fixed metric shape). */}
            {draft.source === 'events'
              && (
                draft.type === 'big_number'
                || draft.type === 'distribution'
                || (draft.type === 'breakdown' && ['standard', 'attribution'].includes(draft.viz_config.mode || 'standard'))
              )
              && (
              <FormControl>
                <FormLabel fontSize="sm">
                  {draft.type === 'big_number' ? 'Show on this card' : 'Metrics per bar'}
                </FormLabel>
                {(() => {
                  const cfgMetrics = Array.isArray(draft.viz_config.metrics) ? draft.viz_config.metrics : null;
                  const selected = cfgMetrics
                    ? cfgMetrics.map((m) => m.agg)
                    : [draft.viz_config.metric || 'count'];
                  const sumProp = cfgMetrics
                    ? cfgMetrics.find((m) => m.agg === 'sum')?.property
                    : (draft.viz_config.metric === 'sum' ? draft.viz_config.property : undefined);
                  const avgProp = cfgMetrics
                    ? cfgMetrics.find((m) => m.agg === 'avg')?.property
                    : (draft.viz_config.metric === 'avg' ? draft.viz_config.property : undefined);

                  // Only genuinely-summable numeric properties (Bug-003 / Bug-015), unioned
                  // across all selected events (Bug-007).
                  const numericish = mergedAttributes.filter(isNumericAttr);

                  const setMetrics = (aggs, sP, aP) => {
                    const built = aggs.map((agg) => {
                      if (agg === 'count')           return { agg, label: 'Event count' };
                      if (agg === 'unique_contacts') return { agg, label: 'Unique customers' };
                      if (agg === 'sum')             return { agg, property: sP || '', label: sP ? `Sum of ${sP}` : 'Sum' };
                      if (agg === 'avg')             return { agg, property: aP || '', label: aP ? `Avg of ${aP}` : 'Avg' };
                      return { agg };
                    });
                    if (built.length === 1) {
                      const m = built[0];
                      setDraft((d) => ({ ...d, viz_config: { ...d.viz_config,
                        metrics: undefined, metric: m.agg, property: m.property || undefined } }));
                    } else {
                      setDraft((d) => ({ ...d, viz_config: { ...d.viz_config,
                        metrics: built, metric: undefined, property: undefined } }));
                    }
                  };
                  const toggle = (agg, on) => {
                    const next = on ? Array.from(new Set([...selected, agg])) : selected.filter((x) => x !== agg);
                    if (next.length === 0) return;
                    setMetrics(next, sumProp, avgProp);
                  };

                  return (
                    <>
                      <CheckboxGroup colorScheme="blue" /* closest available to var(--brand-primary) */>
                        <VStack align="start" spacing={2}>
                          <Checkbox isChecked={selected.includes('count')} onChange={(e) => toggle('count', e.target.checked)}>
                            <Text fontSize="sm">Event count <Text as="span" fontSize="xs" color="gray.500">— how many events happened</Text></Text>
                          </Checkbox>
                          <Checkbox isChecked={selected.includes('unique_contacts')} onChange={(e) => toggle('unique_contacts', e.target.checked)}>
                            <Text fontSize="sm">Unique customers <Text as="span" fontSize="xs" color="gray.500">— how many distinct people</Text></Text>
                          </Checkbox>
                          <Checkbox isChecked={selected.includes('sum')} onChange={(e) => toggle('sum', e.target.checked)}>
                            <Text fontSize="sm">Sum of a numeric property <Text as="span" fontSize="xs" color="gray.500">— e.g. total spend in PKR</Text></Text>
                          </Checkbox>
                          {/* Property to sum — directly under the Sum checkbox. */}
                          {selected.includes('sum') && (
                            <Box pl={6} w="100%">
                              <FormLabel fontSize="xs">Property to sum {attrsLoading && <Text as="span" color="gray.500">— loading…</Text>}</FormLabel>
                              <Select size="sm" value={sumProp || ''} onChange={(e) => setMetrics(selected, e.target.value, avgProp)}
                                placeholder={numericish.length ? 'Pick a property' : 'Pick event first'}>
                                {sumProp && !numericish.some(a => a.name === sumProp) && <option value={sumProp}>{sumProp} (existing)</option>}
                                {numericish.map((a) => <option key={a.name} value={a.name}>{a.name} ({a.type})</option>)}
                              </Select>
                            </Box>
                          )}
                          <Checkbox isChecked={selected.includes('avg')} onChange={(e) => toggle('avg', e.target.checked)}>
                            <Text fontSize="sm">Average of a numeric property <Text as="span" fontSize="xs" color="gray.500">— e.g. avg ticket size</Text></Text>
                          </Checkbox>
                          {/* Property to average — directly under the Average checkbox. */}
                          {selected.includes('avg') && (
                            <Box pl={6} w="100%">
                              <FormLabel fontSize="xs">Property to average {attrsLoading && <Text as="span" color="gray.500">— loading…</Text>}</FormLabel>
                              <Select size="sm" value={avgProp || ''} onChange={(e) => setMetrics(selected, sumProp, e.target.value)}
                                placeholder={numericish.length ? 'Pick a property' : 'Pick event first'}>
                                {avgProp && !numericish.some(a => a.name === avgProp) && <option value={avgProp}>{avgProp} (existing)</option>}
                                {numericish.map((a) => <option key={a.name} value={a.name}>{a.name} ({a.type})</option>)}
                              </Select>
                            </Box>
                          )}
                        </VStack>
                      </CheckboxGroup>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Tick more than one to show multiple metrics together (e.g. Count + Sum = transactions + total spend).
                      </Text>
                    </>
                  );
                })()}
              </FormControl>
            )}

            {/* ─── BREAKDOWN — mode-routed forms ─── */}
            {(() => {
              if (!(draft.source === 'events' && draft.type === 'breakdown')) return null;
              const mode = draft.viz_config.mode || 'standard';
              const vc = draft.viz_config;
              const eventOptions = events.map((e) => ({ value: e.name, label: e.display_name || e.name }));
              const attrOptions = [
                ...TOP_LEVEL_COLUMNS.map((a) => ({ value: a.name, label: a.name })),
                ...mergedAttributes.map((a) => ({ value: a.name, label: `${a.name}${a.type ? ` (${a.type})` : ''}` })),
              ];
              const attrOptionsFor = (ev) => {
                const list = ev ? (attrsCache[ev] || []) : [];
                return [
                  ...TOP_LEVEL_COLUMNS.map((a) => ({ value: a.name, label: a.name })),
                  ...list.map((a) => ({ value: a.name, label: `${a.name}${a.type ? ` (${a.type})` : ''}` })),
                ];
              };

              // ── STANDARD ──
              if (mode === 'standard') {
                return (
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">
                      Break down by {attrsLoading && <Text as="span" color="gray.500" fontSize="xs">— loading…</Text>}
                    </FormLabel>
                    <SearchableSelect value={vc.property || ''} onChange={(v) => setVc('property', v)}
                      options={attrOptions} placeholder="Pick a column or event property" />
                    <HStack mt={3} align="end">
                      <Box>
                        <FormLabel fontSize="sm">Top N</FormLabel>
                        <NumberInput min={1} max={50} value={vc.top_n || 10}
                          onChange={(_, n) => setVc('top_n', isNaN(n) ? 10 : n)}>
                          <NumberInputField />
                        </NumberInput>
                      </Box>
                      <Box flex="1">
                        <FormLabel fontSize="sm">Orientation</FormLabel>
                        <Select value={vc.orientation || 'horizontal'} onChange={(e) => setVc('orientation', e.target.value)}>
                          <option value="horizontal">Horizontal bars</option>
                          <option value="vertical">Vertical columns</option>
                        </Select>
                      </Box>
                    </HStack>
                    {Array.isArray(vc.metrics) && vc.metrics.length > 1 && (
                      <FormControl mt={3}>
                        <FormLabel fontSize="sm">Display</FormLabel>
                        <Select value={vc.display || 'table'} onChange={(e) => setVc('display', e.target.value)}>
                          <option value="table">Table</option>
                          <option value="cards">Cards (one card per row)</option>
                        </Select>
                      </FormControl>
                    )}
                    {vc.property === 'event' && Array.isArray(vc.events) && vc.events.length > 0 && (
                      <Checkbox mt={3} isChecked={!!vc.preserve_order}
                        onChange={(e) => setVc('preserve_order', e.target.checked)}>
                        <Text fontSize="sm">Preserve event order (label as Step N — name)</Text>
                      </Checkbox>
                    )}
                    {Array.isArray(vc.metrics) && vc.metrics.length > 0 && (
                      <Box mt={3}>
                        <FormLabel fontSize="sm">Per-metric grand-total options</FormLabel>
                        <VStack align="start" spacing={1}>
                          {vc.metrics.map((m, i) => (
                            <Checkbox key={i} isChecked={!!m.no_total}
                              onChange={(e) => {
                                const next = vc.metrics.map((mm, j) => j === i ? { ...mm, no_total: e.target.checked } : mm);
                                setVc('metrics', next);
                              }}>
                              <Text fontSize="xs">No grand total for "{m.label || m.agg}"</Text>
                            </Checkbox>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </FormControl>
                );
              }

              // ── DROPOFF / COMBO ── ordered multi-select of events
              if (mode === 'dropoff' || mode === 'combo') {
                const selected = Array.isArray(vc.events)
                  ? vc.events.map((e) => eventOptions.find((o) => o.value === e) || { value: e, label: e })
                  : [];
                return (
                  <>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Funnel events (in order)</FormLabel>
                      <ReactSelect
                        isMulti
                        value={selected}
                        onChange={(picked) => {
                          const list = (picked || []).map((p) => p.value);
                          setDraft((d) => ({
                            ...d,
                            viz_config: { ...d.viz_config, events: list, event: undefined, property: 'event' },
                          }));
                        }}
                        options={eventOptions}
                        placeholder="Pick 2+ events in order"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={RS_STYLES}
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Order matters — drop-off is calculated stage → next stage.
                      </Text>
                    </FormControl>
                    {mode === 'dropoff' && (
                      <FormControl>
                        <FormLabel fontSize="sm">Orientation</FormLabel>
                        <Select value={vc.orientation || 'horizontal'} onChange={(e) => setVc('orientation', e.target.value)}>
                          <option value="horizontal">Horizontal bars</option>
                          <option value="vertical">Vertical columns</option>
                        </Select>
                      </FormControl>
                    )}
                  </>
                );
              }

              // ── SWEEP ── full sweep analyzer
              if (mode === 'sweep') {
                const srcEv = vc.source_event;
                const sinkEv = vc.sink_event;
                // Lazy-load attrs for src + sink
                if (srcEv) fetchAttrsFor(srcEv);
                if (sinkEv) fetchAttrsFor(sinkEv);
                return (
                  <VStack align="stretch" spacing={3}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Source event</FormLabel>
                      <SearchableSelect value={srcEv || ''}
                        onChange={(v) => { setVc('source_event', v); fetchAttrsFor(v); }}
                        options={eventOptions} placeholder="e.g. credit_received" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Source filter (optional)</FormLabel>
                      <HStack>
                        <Box flex="1">
                          <SearchableSelect
                            value={vc.source_filter?.key || ''}
                            onChange={(v) => setVc('source_filter', { ...(vc.source_filter || {}), key: v })}
                            options={attrOptionsFor(srcEv)}
                            placeholder="Property" />
                        </Box>
                        <Box flex="1">
                          <Input size="sm" placeholder="= value (e.g. salary)"
                            value={vc.source_filter?.value || ''}
                            onChange={(e) => setVc('source_filter', { ...(vc.source_filter || {}), value: e.target.value })} />
                        </Box>
                      </HStack>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Source amount property</FormLabel>
                      <SearchableSelect
                        value={vc.source_amount_key || 'trans_amount'}
                        onChange={(v) => setVc('source_amount_key', v)}
                        options={attrOptionsFor(srcEv)} placeholder="trans_amount" />
                    </FormControl>

                    <Divider />

                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Sink event</FormLabel>
                      <SearchableSelect value={sinkEv || ''}
                        onChange={(v) => { setVc('sink_event', v); fetchAttrsFor(v); }}
                        options={eventOptions} placeholder="e.g. transfer_sent" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Sink exclude (optional, "!= value")</FormLabel>
                      <HStack>
                        <Box flex="1">
                          <SearchableSelect
                            value={vc.sink_exclude?.key || ''}
                            onChange={(v) => setVc('sink_exclude', { ...(vc.sink_exclude || {}), key: v })}
                            options={attrOptionsFor(sinkEv)}
                            placeholder="Property" />
                        </Box>
                        <Box flex="1">
                          <Input size="sm" placeholder="!= value (e.g. xBank)"
                            value={vc.sink_exclude?.value || ''}
                            onChange={(e) => setVc('sink_exclude', { ...(vc.sink_exclude || {}), value: e.target.value })} />
                        </Box>
                      </HStack>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Sink amount property</FormLabel>
                      <SearchableSelect
                        value={vc.sink_amount_key || 'trans_amount'}
                        onChange={(v) => setVc('sink_amount_key', v)}
                        options={attrOptionsFor(sinkEv)} placeholder="trans_amount" />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Group by (sink-side property)</FormLabel>
                      <SearchableSelect
                        value={vc.sink_breakdown || ''}
                        onChange={(v) => setVc('sink_breakdown', v)}
                        options={attrOptionsFor(sinkEv)} placeholder="e.g. bank_code" />
                    </FormControl>

                    <HStack>
                      <FormControl>
                        <FormLabel fontSize="sm">Window (days)</FormLabel>
                        <NumberInput min={1} max={14} value={vc.window_days || 3}
                          onChange={(_, n) => setVc('window_days', isNaN(n) ? 3 : n)}>
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Completeness %</FormLabel>
                        <NumberInput min={1} max={200} value={vc.completeness_pct || 100}
                          onChange={(_, n) => setVc('completeness_pct', isNaN(n) ? 100 : n)}>
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Top N</FormLabel>
                        <NumberInput min={1} max={50} value={vc.top_n || 12}
                          onChange={(_, n) => setVc('top_n', isNaN(n) ? 12 : n)}>
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>
                    </HStack>
                    <FormControl>
                      <FormLabel fontSize="sm">Display</FormLabel>
                      <Select value={vc.display || 'table'} onChange={(e) => setVc('display', e.target.value)}>
                        <option value="table">Table</option>
                        <option value="cards">Cards (one card per group)</option>
                      </Select>
                    </FormControl>
                  </VStack>
                );
              }

              // ── ATTRIBUTION ──
              if (mode === 'attribution') {
                const viaEv = vc.attribute_via?.event;
                if (viaEv) fetchAttrsFor(viaEv);
                return (
                  <>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Break down by (property on main event)</FormLabel>
                      <SearchableSelect value={vc.property || ''} onChange={(v) => setVc('property', v)}
                        options={attrOptions} placeholder="Pick a column or event property" />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Attribute via — event</FormLabel>
                      <SearchableSelect
                        value={viaEv || ''}
                        onChange={(v) => { setVc('attribute_via', { ...(vc.attribute_via || {}), event: v }); fetchAttrsFor(v); }}
                        options={eventOptions} placeholder="Pick the event whose property attributes this metric" />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Attribute via — property</FormLabel>
                      <SearchableSelect
                        value={vc.attribute_via?.property || ''}
                        onChange={(v) => setVc('attribute_via', { ...(vc.attribute_via || {}), property: v })}
                        options={attrOptionsFor(viaEv)} placeholder="Property on that event" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Top N</FormLabel>
                      <NumberInput min={1} max={50} value={vc.top_n || 12}
                        onChange={(_, n) => setVc('top_n', isNaN(n) ? 12 : n)}>
                        <NumberInputField />
                      </NumberInput>
                    </FormControl>
                  </>
                );
              }
              return null;
            })()}

            {/* Time series: optional breakdown dimension */}
            {draft.source === 'events' && draft.type === 'time_series' && (
              <FormControl>
                <FormLabel fontSize="sm">
                  Break down by (optional) {attrsLoading && <Text as="span" color="gray.500" fontSize="xs">— loading…</Text>}
                </FormLabel>
                <Select value={draft.viz_config.breakdown || ''} onChange={(e) => setVc('breakdown', e.target.value || undefined)}
                  placeholder="— None (single series) —">
                  <optgroup label="Top-level columns">
                    {TOP_LEVEL_COLUMNS.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
                  </optgroup>
                  <optgroup label="Event properties (variables)">
                    {draft.viz_config.breakdown && !mergedAttributes.some(a => a.name === draft.viz_config.breakdown) && !TOP_LEVEL_COLUMNS.some(a => a.name === draft.viz_config.breakdown) && (
                      <option value={draft.viz_config.breakdown}>{draft.viz_config.breakdown} (existing)</option>
                    )}
                    {mergedAttributes.map((a) => <option key={a.name} value={a.name}>{a.name} {a.type ? `(${a.type})` : ''}</option>)}
                  </optgroup>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>Splits the line into one series per distinct value (e.g. one line per channel).</Text>
              </FormControl>
            )}

            {/* Distribution: numeric property + buckets editor */}
            {draft.source === 'events' && draft.type === 'distribution' && (
              <>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Numeric property to bucket {attrsLoading && <Text as="span" color="gray.500" fontSize="xs">— loading…</Text>}</FormLabel>
                  <Select value={draft.viz_config.property || ''} onChange={(e) => setVc('property', e.target.value)}
                    placeholder={mergedAttributes.length ? 'Pick a numeric property' : 'Pick event first'}>
                    {draft.viz_config.property && !mergedAttributes.some(a => a.name === draft.viz_config.property) && (
                      <option value={draft.viz_config.property}>{draft.viz_config.property} (existing)</option>
                    )}
                    {mergedAttributes.filter(isNumericAttr).map((a) => (
                      <option key={a.name} value={a.name}>{a.name} {a.type ? `(${a.type})` : ''}</option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    e.g. <em>amount</em> to bucket transactions into tiers like 0–1k, 1k–10k, 10k+.
                  </Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Buckets</FormLabel>
                  {(() => {
                    const buckets = Array.isArray(draft.viz_config.buckets) ? draft.viz_config.buckets : [];
                    const setB = (next) => setVc('buckets', next);
                    return (
                      <VStack align="stretch" spacing={2}>
                        {buckets.length === 0 && (
                          <Text fontSize="xs" color="gray.500">No buckets defined — backend will use default ranges. Click "Add bucket" to customise.</Text>
                        )}
                        {buckets.map((b, i) => (
                          <HStack key={i} spacing={2}>
                            <Input size="sm" placeholder="Label (e.g. 1k–10k)" value={b.label || ''}
                              onChange={(e) => { const n = [...buckets]; n[i] = { ...n[i], label: e.target.value }; setB(n); }} />
                            <Input size="sm" type="number" placeholder="Min" value={b.min ?? ''}
                              onChange={(e) => { const n = [...buckets]; n[i] = { ...n[i], min: e.target.value === '' ? undefined : Number(e.target.value) }; setB(n); }} />
                            <Input size="sm" type="number" placeholder="Max" value={b.max ?? ''}
                              onChange={(e) => { const n = [...buckets]; n[i] = { ...n[i], max: e.target.value === '' ? undefined : Number(e.target.value) }; setB(n); }} />
                            <IconButton size="sm" variant="ghost" aria-label="Remove bucket" icon={<FiTrash2 />}
                              onClick={() => setB(buckets.filter((_, j) => j !== i))} />
                          </HStack>
                        ))}
                        <Button size="sm" leftIcon={<FiPlus />} variant="outline"
                          onClick={() => setB([...buckets, { label: '', min: undefined, max: undefined }])}>
                          Add bucket
                        </Button>
                        <Text fontSize="xs" color="gray.500">Leave Min blank for "−∞" or Max blank for "+∞".</Text>
                      </VStack>
                    );
                  })()}
                </FormControl>
              </>
            )}

            {draft.source === 'events' && draft.type === 'funnel' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Funnel steps (in order)</FormLabel>
                {(() => {
                  const stepOpts = events.map((e) => ({ value: e.name, label: e.display_name || e.name }));
                  const selected = Array.isArray(draft.viz_config.steps)
                    ? draft.viz_config.steps.map((e) => stepOpts.find((o) => o.value === e) || { value: e, label: e })
                    : [];
                  return (
                    <ReactSelect
                      isMulti
                      value={selected}
                      onChange={(picked) => {
                        const list = (picked || []).map((p) => p.value);
                        setDraft((d) => ({
                          ...d,
                          viz_config: { ...d.viz_config, steps: list, stepsText: undefined },
                        }));
                      }}
                      options={stepOpts}
                      placeholder="Pick 2+ events in order"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={RS_STYLES}
                    />
                  );
                })()}
                <Text fontSize="xs" color="gray.500" mt={1}>Min 2 events — order matters (left → right = top → bottom of funnel).</Text>
              </FormControl>
            )}

            {draft.source === 'events' && (
              <HStack>
                <FormControl>
                  <FormLabel fontSize="sm">Time range</FormLabel>
                  <Select value={draft.viz_config.time_frame || 'last_30_days'} onChange={(e) => setVc('time_frame', e.target.value)}>
                    {TIME_FRAMES.map((tf) => <option key={tf.value} value={tf.value}>{tf.label}</option>)}
                  </Select>
                </FormControl>
                {draft.type === 'big_number' && (
                  <FormControl>
                    <FormLabel fontSize="sm">Compare to</FormLabel>
                    <Select value={draft.viz_config.compare_to || 'previous_period'} onChange={(e) => setVc('compare_to', e.target.value)}>
                      <option value="previous_period">Previous period</option>
                      <option value="none">No comparison</option>
                    </Select>
                  </FormControl>
                )}
              </HStack>
            )}

            {draft.source === 'contacts' && draft.type === 'profile_breakdown' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Profile key</FormLabel>
                <Select value={draft.viz_config.profile_key || ''} onChange={(e) => setVc('profile_key', e.target.value)}
                  placeholder={profileKeys.length ? 'Pick a profile key' : 'No profile keys found'}>
                  {draft.viz_config.profile_key && !profileKeys.includes(draft.viz_config.profile_key) && (
                    <option value={draft.viz_config.profile_key}>{draft.viz_config.profile_key} (existing)</option>
                  )}
                  {profileKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Distribution of contacts across this profile attribute (e.g. gender, region, age band).
                </Text>
                <FormLabel fontSize="sm" mt={3}>Top N</FormLabel>
                <NumberInput min={1} max={20} value={draft.viz_config.top_n || 6}
                  onChange={(_, n) => setVc('top_n', isNaN(n) ? 6 : n)}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            )}

            {draft.source === 'cohort_membership' && draft.type === 'segment_leaderboard' && (
              <FormControl>
                <FormLabel fontSize="sm">Top N cohorts</FormLabel>
                <NumberInput min={1} max={50} value={draft.viz_config.top_n || 10}
                  onChange={(_, n) => setVc('top_n', isNaN(n) ? 10 : n)}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Refresh frequency</FormLabel>
              <Select value={draft.refresh_frequency} onChange={(e) => setField('refresh_frequency', e.target.value)}>
                <option value="realtime">Realtime (15 s)</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily (default)</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </Select>
            </FormControl>
          </VStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button
            bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} _active={{ bg: 'var(--brand-primary-active)' }}
            isLoading={saving} onClick={save}
          >
            {mode === 'edit' ? 'Save changes' : 'Add tile'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
